import { ArrowUpRight } from "lucide-react";

export function Hero({ stats }) {
    return (
        <header className="relative overflow-hidden border-b border-border" data-testid="hero-section">
            {/* Top utility bar */}
            <div className="flex items-center justify-between px-6 md:px-12 py-4 border-b border-border bg-background">
                <div className="flex items-center gap-3">
                    <span className="block h-2.5 w-2.5 bg-primary" aria-hidden />
                    <span className="font-mono-label text-xs">Lyve <span className="font-editorial text-primary">Classes</span></span>
                </div>
                <div className="hidden md:flex items-center gap-6">
                    <span className="font-mono-label text-xs text-muted-foreground">
                        Vol. 01 · Feb 2026
                    </span>
                    <span className="font-mono-label text-xs text-muted-foreground">
                        {stats?.total_classes ?? "—"} classes
                    </span>
                </div>
            </div>

            <div className="grid md:grid-cols-12 gap-0 bg-grain">
                <div className="md:col-span-7 px-6 md:px-12 py-16 md:py-24 flex flex-col justify-between">
                    <div className="rise">
                        <p className="font-mono-label text-xs text-muted-foreground mb-6">
                            Issue 001 — A field guide
                        </p>
                        <h1 className="font-display text-5xl sm:text-7xl lg:text-[6.5rem] leading-[0.95]">
                            Strong women,
                            <br />
                            <span className="font-editorial text-primary">sharper</span> classes.
                        </h1>
                        <p className="mt-8 max-w-xl text-base leading-relaxed text-muted-foreground">
                            A curated, verified index of women-focused and women-friendly workout
                            classes across all five boroughs of New York City. Yoga, pilates,
                            boxing, barre, dance, HIIT, prenatal, and beyond.
                        </p>
                    </div>

                    <div className="mt-10 flex flex-wrap gap-x-10 gap-y-4">
                        <Stat label="Classes" value={stats?.total_classes} />
                        <Stat label="Studios" value={stats?.total_studios} />
                        <Stat label="Categories" value={stats?.total_categories} />
                        <Stat label="Boroughs" value={stats?.total_boroughs} />
                    </div>
                </div>

                <div className="md:col-span-5 relative border-l border-border min-h-[320px]">
                    <img
                        src="https://images.unsplash.com/photo-1591504771094-a1ca4de142d7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwzfHx3b21lbiUyMGJveGluZyUyMGZpdG5lc3MlMjBjbGFzc3xlbnwwfHx8fDE3Nzg1NTI2NjZ8MA&ixlib=rb-4.1.0&q=85"
                        alt="Woman boxing"
                        className="absolute inset-0 w-full h-full object-cover grayscale"
                    />
                    <div className="absolute inset-0 bg-foreground/10" />
                    <div className="absolute bottom-0 left-0 right-0 bg-background border-t border-border px-6 py-4 flex items-center justify-between">
                        <span className="font-mono-label text-xs">Published reading</span>
                        <a
                            href="#directory-section"
                            className="font-mono-label text-xs flex items-center hover-invert px-2 py-1 border border-foreground"
                            data-testid="hero-cta"
                        >
                            Browse <ArrowUpRight className="ml-1 h-3 w-3" />
                        </a>
                    </div>
                </div>
            </div>

            {/* Marquee strip */}
            <div className="border-t border-border bg-foreground text-background py-3 overflow-hidden">
                <div className="marquee-track flex gap-12 whitespace-nowrap font-mono-label text-xs">
                    {["a", "b"].flatMap((track) =>
                        [
                            "Yoga",
                            "Boxing",
                            "Pilates",
                            "Barre",
                            "Spin",
                            "HIIT",
                            "Strength",
                            "Dance",
                            "Bootcamp",
                            "CrossFit",
                            "Mobility",
                            "Prenatal",
                            "Postnatal",
                            "Women-only",
                        ].map((c, i) => (
                            <span key={`mq-${track}-${c}-${i}`} className="flex items-center gap-12">
                                <span>{c}</span>
                                <span aria-hidden>·</span>
                            </span>
                        ))
                    )}
                </div>
            </div>
        </header>
    );
}

function Stat({ label, value }) {
    return (
        <div>
            <div className="font-display text-4xl">{value ?? "—"}</div>
            <div className="font-mono-label text-xs text-muted-foreground mt-1">{label}</div>
        </div>
    );
}
